import Mention from '@tiptap/extension-mention'
import { VueRenderer } from '@tiptap/vue-3'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'

import MentionList from '@/tiptap/extensions/components/mentions/MentionList.vue'
import api from '@/plugins/axios'

const HANDBOOK_HREF_RE = /\/handbook\/article\/(\d+)(?:\/|$|[?#])/i

function articleIdFromHref(href) {
  if (typeof href !== 'string') return null
  const match = href.match(HANDBOOK_HREF_RE)
  if (!match) return null
  const n = Number(match[1])
  return Number.isFinite(n) ? n : null
}

/**
 * Factory to create a customized Mention node that:
 * - inserts an atomic <mention> (deletes as a whole on Backspace)
 * - renders as <a> with label text (no leading trigger)
 * - stores id + href so the viewer can open a modal
 * - supports configurable trigger (default '#') and API mapper
 */
export function createMentionExtension({
  char = '#',
  className = 'mention-link',
  apiPath = '/api/handbook-sections/suggestions/',
  hrefBuilder = (item) => `/handbook/article/${item.id}`,
  mapItem = (raw) => ({ id: raw.id, label: raw.title ?? raw.value ?? raw.label ?? String(raw.id) }),
} = {}) {
  const NODE_NAME = 'handbookMention'

  return Mention.extend({
    name: NODE_NAME,

    group: 'inline',
    inline: true,
    selectable: false,
    atom: true, // make it a single unit → deletes in one go

    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (el) => {
            const dataId = Number(el.getAttribute('data-id'))
            if (Number.isFinite(dataId)) return dataId
            const hrefId = articleIdFromHref(el.getAttribute('href'))
            if (hrefId != null) return hrefId
            const deletedId = Number(el.getAttribute('data-deleted-article-id'))
            if (Number.isFinite(deletedId)) return deletedId
            return null
          },
        },
        label: {
          default: null,
          parseHTML: (el) => {
            const text = (el.textContent || '').trim()
            return text || null
          },
        },
        href: {
          default: null,
          parseHTML: (el) => {
            const href = (el.getAttribute('href') || '').trim()
            if (href && href.includes('/handbook/article/')) return href
            const deletedId = Number(el.getAttribute('data-deleted-article-id'))
            if (Number.isFinite(deletedId)) return `/handbook/article/${deletedId}`
            return null
          },
        },
        deleted: {
          default: false,
          parseHTML: (el) => {
            const cls = (el.getAttribute('class') || '').toLowerCase()
            const title = (el.getAttribute('title') || '').toLowerCase()
            const text = (el.textContent || '').toLowerCase()
            return Boolean(
              el.getAttribute('data-deleted-article-id') ||
              cls.includes('deleted-link') ||
              cls.includes('mention-deleted') ||
              title.includes('deleted article') ||
              text.includes('deleted article')
            )
          },
        },
        deletedArticleId: {
          default: null,
          parseHTML: (el) => {
            const explicit = Number(el.getAttribute('data-deleted-article-id'))
            if (Number.isFinite(explicit)) return explicit
            const cls = (el.getAttribute('class') || '').toLowerCase()
            const title = (el.getAttribute('title') || '').toLowerCase()
            const text = (el.textContent || '').toLowerCase()
            const looksDeleted =
              cls.includes('deleted-link') ||
              cls.includes('mention-deleted') ||
              title.includes('deleted article') ||
              text.includes('deleted article')
            if (!looksDeleted) return null
            return articleIdFromHref(el.getAttribute('href'))
          },
        },
      }
    },

    parseHTML() {
      return [
        { tag: 'a[href*="/handbook/article/"]' },
        { tag: 'span[data-deleted-article-id]' },
      ]
    },

    renderHTML({ node, HTMLAttributes }) {
      // Render as a normal <a> (editor CSS will disable pointer events in edit mode)
      const isDeleted = Boolean(node?.attrs?.deleted || node?.attrs?.deletedArticleId)
      const deletedId = node?.attrs?.deletedArticleId ?? node?.attrs?.id ?? null
      const attrs = {
        ...HTMLAttributes,
        href: node?.attrs?.href || (deletedId != null ? `/handbook/article/${deletedId}` : '#'),
        'data-id': node?.attrs?.id ?? '',
        class: [
          HTMLAttributes?.class,
          className,
          'mention',
          'mention-link',
          isDeleted ? 'mention-deleted deleted-link' : '',
        ].filter(Boolean).join(' '),
      }
      if (isDeleted && deletedId != null) attrs['data-deleted-article-id'] = String(deletedId)
      if (isDeleted) attrs.title = 'Deleted article'
      const label = node?.attrs?.label || ''
      return ['a', attrs, label]
    },

    addKeyboardShortcuts() {
      // Single Backspace/Delete removes the whole chip when the cursor is right after it
      return {
        Backspace: () => this.options.editor?.commands?.command?.(({ state, tr }) => {
          const { $from } = state.selection
          const prev = $from.nodeBefore
          if (prev && prev.type && prev.type.name === NODE_NAME) {
            tr.delete($from.pos - prev.nodeSize, $from.pos)
            return true
          }
          return false
        }) ?? false,
        Delete: () => this.options.editor?.commands?.command?.(({ state, tr }) => {
          const { $from } = state.selection
          const next = $from.nodeAfter
          if (next && next.type && next.type.name === NODE_NAME) {
            tr.delete($from.pos, $from.pos + next.nodeSize)
            return true
          }
          return false
        }) ?? false,
      }
    },
  }).configure({
    HTMLAttributes: { class: className },
    suggestion: {
      char,
      items: async ({ query }) => {
        if (!query) return []
        try {
          const url = `${apiPath}?query=${encodeURIComponent(query)}`
          const res = await api.get(url)
          const arr = Array.isArray(res.data) ? res.data : []
          return arr.map((raw) => {
            const mapped = mapItem(raw)
            return { ...mapped, __raw: raw }
          })
        } catch (e) {
          console.error(`[mentions] fetch failed for ${apiPath}`, e)
          return []
        }
      },
      // Insert atomic mention node and drop the trigger symbol
      command: ({ editor, range, props }) => {
        const href = hrefBuilder(props)
        editor
          .chain()
          .focus()
          .insertContentAt({ from: range.from, to: range.to }, [
            {
              type: NODE_NAME,
              attrs: { id: props.id, label: props.label, href, deleted: false, deletedArticleId: null },
            },
            { type: 'text', text: ' ' },
          ])
          .run()
      },
      render: () => {
        let component
        let popup
        return {
          onStart(props) {
            component = new VueRenderer(MentionList, { props, editor: props.editor })
            popup = tippy('body', {
              getReferenceClientRect: props.clientRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
              theme: 'light',
            })
          },
          onUpdate(props) {
            component.updateProps(props)
            popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect })
          },
          onKeyDown(props) {
            if (props.event.key === 'Escape') {
              popup?.[0]?.hide()
              return true
            }
            return component.ref?.onKeyDown(props)
          },
          onExit() {
            // Destroy popup and component if they exist (no-op if already gone)
            const destroySafely = (fn) => {
              try {
                fn && fn();
              } catch (e) {
                // swallow errors from third‑party cleanup to avoid crashing unmount
                // (we log in development for visibility)
                if (process && process.env && process.env.NODE_ENV !== 'production') {
                  // eslint-disable-next-line no-console
                  console.debug('[mentions] cleanup error:', e);
                }
              }
            };

            const t = popup && popup[0];
            destroySafely(() => t && t.destroy());
            destroySafely(() => component && component.destroy());
          },
        }
      },
    },
  })
}
